import { useFragment } from '@apollo/client/react';
import { TeamSpot } from '@optools/components/team-spot';
import { Badge } from '@optools/components/ui/badge';
import {
  BTC_BROADCAST_FRAGMENT,
  BTC_GAME_FRAGMENT,
} from '@optools/services/operator-gateway/btc/fragments';
import { MediaType } from '@optools/services/operator-gateway/types/generated/graphql';
import { BroadcastForm } from '@optools/tools/btc-tools/components/broadcasts/broadcast-form';
import { broadcastsParamsSchema } from '@optools/tools/btc-tools/schemas/broadcast';
import { authorizedRouteLoader } from '@optools/utils/auth/authorized-route-loader';
import { getTeamByFeedType } from '@optools/utils/pipeline';
import { createFileRoute, Link, useParams, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Database, RadioTower, Speaker, Video } from 'lucide-react';

/* eslint-disable perfectionist/sort-objects */
export const Route = createFileRoute('/_auth/btc-tools/schedule/$gamePk/broadcasts/$mediaId')({
  component: RouteComponent,
  params: { parse: zodValidator(broadcastsParamsSchema).parse },
  beforeLoad: authorizedRouteLoader('btcTools.schedule.broadcastDetail', {
    unauthorizedRedirect: ({ location }) => {
      return location.pathname.substring(0, location.pathname.lastIndexOf('/'));
    },
  }),
});
/* eslint-enable perfectionist/sort-objects */

export function RouteComponent() {
  const { date } = useSearch({ from: '/_auth/btc-tools/schedule/$gamePk/broadcasts/$mediaId' });
  const { gamePk, mediaId } = useParams({
    from: '/_auth/btc-tools/schedule/$gamePk/broadcasts/$mediaId',
  });

  const gameFragment = useFragment({
    fragment: BTC_GAME_FRAGMENT,
    fragmentName: 'BTCGameFields',
    from: { __typename: 'Game', gamePk },
  });

  const broadcastFragment = useFragment({
    fragment: BTC_BROADCAST_FRAGMENT,
    from: { __typename: 'Broadcast', mediaId },
  });

  if (!broadcastFragment.complete || !gameFragment.complete) {
    return <h1>Broadcast with mediaId: {mediaId} cannot be found.</h1>;
  }

  const { data: game } = gameFragment;
  const { data: broadcast } = broadcastFragment;

  const { away, home } = game;
  const { callSign, feedType, mediaType, name } = broadcast;

  const team = getTeamByFeedType({ away, feedType, home });

  return (
    <section>
      <header className="mt-4 mb-12 flex items-start justify-between">
        <div>
          <div className="mb-4 flex items-center gap-4">
            <div>
              <h1 className="mb-1 flex items-center gap-1 text-xl font-bold">
                <RadioTower />
                {callSign}
              </h1>
              <p className="text-xs text-primary/70">{name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge>
              {mediaType === MediaType.VIDEO ? <Video /> : <Speaker />} {mediaType}
            </Badge>
            <Badge className="font-mono">
              <Database />
              <Link
                className="hover:underline"
                search={{ tag: { name: 'mediaId', value: mediaId } }}
                to="/audit/audits">
                {mediaId}
              </Link>
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <TeamSpot showAbbreviation={false} size={32} team={team} />
          <span className="font-semibold">{feedType}</span>
        </div>
      </header>

      <BroadcastForm broadcast={broadcast} date={date} gamePk={gamePk} />
    </section>
  );
}
